import { ChevronLeft, ContentCopy } from '@ixigo/icons';
import Chip from '@ixigo/iui/Chip';
import IconButton from '@ixigo/iui/IconButton';
import Typography, { TypographyProps } from '@ixigo/iui/Typography';
import clsx from 'clsx';
import { Fragment, useState } from 'react';
import { TestId } from '../../enums/test';
import { StepColor, StepType } from '../../enums/widget';
import check from '../../images/check.svg';
import { copyText } from '../../utils/common';
import { getRefundCreditText } from '../../utils/conversation';
import Dot from './Dot';
import Separator from './Separator';
import { TimelineProps } from './index.types';

function Timeline({ data, defaultCollapsed = false }: TimelineProps) {
    const [collapsed, setCollapsed] = useState(defaultCollapsed);

    const getStepProperties = (step: TimelineProps['data']['steps'][0], index: number) => {
        const { color, type } = step;
        let icon;
        let fontWeight: TypographyProps['fontWeight'] = 'normal';
        let fontClass = 'text-charcoal-800';
        let lineClass = 'bg-green-500';

        if (type === StepType.PAST) {
            icon = <img src={check} alt='Success' className='z-10' />;
        } else if (type === StepType.FUTURE) {
            fontClass = 'text-charcoal-600';
            icon = <Dot />;
        } else {
            fontWeight = 'medium';
            if (index === data.steps.length - 1) {
                icon = <img src={check} alt='Success' className='z-10' />;
            } else {
                icon = <Dot variant='success' />;
            }
        }

        if (color === StepColor.WARN) {
            icon = <Dot variant='warn' />;
        }

        const nextStep = data.steps[index + 1];
        if (nextStep) {
            const { color: nextColor, type: nextType } = nextStep;
            if (nextType === StepType.FUTURE) {
                lineClass = 'bg-charcoal-400';
            } else if (nextColor === StepColor.WARN) {
                lineClass = 'bg-yellow-700';
            }
        }

        return { icon, fontWeight, fontClass, lineClass };
    };

    const renderSteps = () => (
        <div className='relative transition-all'>
            <IconButton
                variant='flat'
                color='neutral'
                onClick={() => setCollapsed((prev) => !prev)}
                className='!absolute top-0 right-0 w-30 rounded-full z-10'
            >
                <ChevronLeft className={clsx('shrink-0', collapsed ? '-rotate-90' : 'rotate-90')} />
            </IconButton>
            {data.steps.map((step, index) => {
                const { arn, subText, text, type } = step;
                const { icon, fontWeight, fontClass, lineClass } = getStepProperties(step, index);

                return (
                    <div
                        key={step.text}
                        className={clsx(
                            'relative flex gap-10 items-start pt-5 pb-15',
                            !collapsed && '[&:last-of-type_.line]:hidden',
                            collapsed && type !== StepType.PRESENT && 'hidden',
                        )}
                        data-testid={TestId.TIMELINE_STEP}
                    >
                        {icon}
                        <div className='flex flex-col gap-5'>
                            <Typography variant='body-md' fontWeight={fontWeight} className={fontClass}>
                                {text}
                            </Typography>
                            <Typography variant='body-xs' className='text-charcoal-600'>
                                {subText}
                            </Typography>
                            {arn && (
                                <Chip
                                    variant='soft'
                                    color='primary'
                                    iconRight={
                                        <IconButton
                                            color='neutral'
                                            size='sm'
                                            variant='flat'
                                            className='!min-h-0 !p-0'
                                            onClick={() => copyText(arn)}
                                        >
                                            <ContentCopy fontSize={20} />
                                        </IconButton>
                                    }
                                >
                                    ARN: {arn}
                                </Chip>
                            )}
                        </div>
                        <div className={clsx('line absolute top-5 left-15 w-px h-full', lineClass)} />
                    </div>
                );
            })}
        </div>
    );

    const renderSources = () => {
        if (!data.sources?.length) return null;

        return (
            <div className='flex flex-col gap-10 border p-15 border-charcoal-100 rounded-20'>
                <Typography variant='body-sm'>{getRefundCreditText(data.isRefundSuccessful)}</Typography>
                <div className='flex flex-col gap-5'>
                    {data.sources.map((item, index) => (
                        <Fragment key={item.text}>
                            {index !== 0 && <Separator />}
                            <div className='flex justify-between gap-5 py-5' data-testid={TestId.TIMELINE_SOURCE}>
                                <div className='flex items-center gap-5'>
                                    <img src={item.imageURL} alt={item.text} className='w-20' />
                                    <Typography variant='body-md'>{item.text}</Typography>
                                </div>
                                <Typography variant='body-md' fontWeight='medium'>
                                    ₹{item.amount}
                                </Typography>
                            </div>
                        </Fragment>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className='flex flex-col gap-10' data-testid={TestId.TIMELINE}>
            {renderSteps()}
            {renderSources()}
        </div>
    );
}

export default Timeline;
